/**
 * Billing PSP client-config (TMMAPP-0015) — `GET /v1/me/billing/client-config` (bearer).
 * `providerFields` is never read (other-PSP ids for the console). The publishable key is a
 * public value and is never logged.
 */
import type { BillingClientConfig as Wire } from '@/api/generated';
import { request } from './client';
import { ErrorCodes, isApiError } from './errors';

export interface StripeClientConfig {
  provider: 'stripe';
  publishableKey: string;
  stripeAccount: string | null;
  environment: string;
  flow: string | null;
  /**
   * How charges appear on the card statement (TMMAPI-0156 U2, pay-api's approved descriptor); null
   * when not served, and the line is omitted. Never hard-coded.
   */
  statementDescriptor: string | null;
  /**
   * A flag, never an address (TMMAPI-0156 U2): `required` while Stripe Tax is on for the tenant.
   * The card form always collects the full billing address; tmm-app never receives one.
   */
  billingAddress: 'required' | null;
  /**
   * The customer emails Stripe itself sends in the seller's name (TMMAPI-0159, pay-api §5), display
   * only: the known tokens, in served order; empty when none (or none known) is served.
   */
  customerEmails: StripeCustomerEmail[];
}

/** pay-api's `stripeCustomerEmails` tokens (PAYAPI-0164). */
export const STRIPE_CUSTOMER_EMAILS = [
  'receipts',
  'refunds',
  'failed_payment',
  'expiring_card',
  'payment_confirmation',
  'trial_ending',
] as const;
export type StripeCustomerEmail = (typeof STRIPE_CUSTOMER_EMAILS)[number];

function normalizeCustomerEmails(value: readonly string[] | null): StripeCustomerEmail[] {
  const known = new Set<string>(STRIPE_CUSTOMER_EMAILS);
  const out: StripeCustomerEmail[] = [];
  for (const token of value ?? []) {
    if (known.has(token) && !out.includes(token as StripeCustomerEmail)) {
      out.push(token as StripeCustomerEmail);
    }
  }
  return out;
}

export type BillingClientConfigResult =
  | { kind: 'stripe'; config: StripeClientConfig }
  | { kind: 'unsupported'; provider: string }
  | { kind: 'unavailable' };

export function normalizeBillingClientConfig(w: Wire): BillingClientConfigResult {
  if (w.provider !== 'stripe') {
    return { kind: 'unsupported', provider: w.provider || 'unknown' };
  }
  const publishableKey = w.publishableKey?.trim() ?? '';
  if (publishableKey.length === 0) {
    return { kind: 'unavailable' };
  }
  return {
    kind: 'stripe',
    config: {
      provider: 'stripe',
      publishableKey,
      stripeAccount: w.stripeAccount || null,
      environment: w.environment,
      flow: w.flow ?? null,
      statementDescriptor: w.statementDescriptor?.trim() || null,
      billingAddress: w.billingAddress === 'required' ? 'required' : null,
      customerEmails: normalizeCustomerEmails(w.stripeCustomerEmails),
    },
  };
}

export const billingClientConfigApi = {
  async get(): Promise<BillingClientConfigResult> {
    try {
      return normalizeBillingClientConfig(
        await request<Wire>('GET', '/v1/me/billing/client-config'),
      );
    } catch (error) {
      if (isApiError(error) && error.code === ErrorCodes.PAY_API_UNAVAILABLE) {
        return { kind: 'unavailable' };
      }
      throw error;
    }
  },
};
